import { yupResolver } from '@hookform/resolvers/yup';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import LoadingButton from '@mui/lab/LoadingButton';
import {
	AppBar,
	Box,
	Button,
	Checkbox,
	Chip,
	FormControl,
	FormGroup,
	FormHelperText,
	IconButton,
	InputLabel,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Toolbar,
	Typography,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import type { Coordinate } from 'ol/coordinate';
import React, { useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { mapFormValidationSchema } from '../../app/forms/mapForm';
import { useAppSelector } from '../../app/hooks/store';
import type { MapModifiableProps, MapaMap, NewMap } from '../../app/services/maps';
import type { SymbologyProps } from '../../app/services/schemas';
import { DialogWithTransition } from '../../app/ui/dialog';
import DiscardChangesDialog from '../../app/ui/discardChangesDialog';
import FormSectionHeading from '../../app/ui/formSectionHeading';
import TextFieldWithout1Password from '../../app/ui/textFieldWithout1Password';
import { selectAllFeatures } from '../features/featuresSlice';
import { getSchemaIdsUsedByFeatures } from '../schemas/schemaHelpers';
import { selectAllFeatureSchemas } from '../schemas/schemasSlice';
import SymbologyFieldEditor from '../symbology/symbologyFieldEditor';
import MapStartingLocationEditor from './mapStartingLocationEditor';

interface Props {
	map?: MapaMap;
	isMapSaving: boolean;
	onDoneAdding?: (map: NewMap) => void;
	onDoneEditing?: (map: MapaMap) => void;
}

function MapForm(props: Props) {
	const { map, isMapSaving, onDoneAdding, onDoneEditing } = props;

	const navigate = useNavigate();

	const schemas = useAppSelector(selectAllFeatureSchemas);

	const {
		watch,
		setValue,
		handleSubmit,
		control,
		formState: { errors, isDirty },
	} = useForm<MapModifiableProps>({
		resolver: yupResolver(mapFormValidationSchema),
		defaultValues: {
			name: map?.name || '',
			default_symbology: map?.default_symbology || undefined,
			hero_icon: map?.hero_icon || undefined,
			available_schema_ids: map?.available_schema_ids || [],
			starting_location: map?.starting_location || null,
			location_search_enabled: map?.location_search_enabled || false,
		},
	});

	const { hero_icon, default_symbology, available_schema_ids, starting_location, location_search_enabled } = watch();

	// ######################
	// Map Hero Icon
	// ######################
	const [isSettingMapHeroIcon, setIsSettingMapHeroIcon] = useState(false);

	const onSetMapHeroIcon = () => {
		setIsSettingMapHeroIcon(true);
	};

	const onDoneSettingMapHeroIcon = (symbolField: SymbologyProps) => {
		setValue('hero_icon', symbolField, { shouldDirty: true });
		setIsSettingMapHeroIcon(false);
	};

	const onCancelSettingMapHeroIcon = () => setIsSettingMapHeroIcon(false);
	// ######################
	// Map Hero Icon (End)
	// ######################

	// ######################
	// Default Symbology
	// ######################
	const [isSettingDefaultSymbol, setIsSettingDefaultSymbol] = useState(false);

	const onSetDefaultSymbolField = () => {
		setIsSettingDefaultSymbol(true);
	};

	const onDoneSettingDefaultSymbol = (symbolField: SymbologyProps) => {
		setValue('default_symbology', symbolField, { shouldDirty: true });
		setIsSettingDefaultSymbol(false);
	};

	const onCancelSettingDefaultSymbol = () => setIsSettingDefaultSymbol(false);
	// ######################
	// Default Symbology (End)
	// ######################

	// ######################
	// Schema Removal Guard
	// ######################
	const features = useAppSelector(selectAllFeatures);

	const schemaIdsUsedOnMap = getSchemaIdsUsedByFeatures(features);
	// ######################
	// Schema Removal Guard (End)
	// ######################

	// ######################
	// Starting Location
	// ######################
	const [isSettingMapStartingLocation, setIsSettingMapStartingLocation] = useState(false);

	const onClickChooseMapStartingLocation = () => setIsSettingMapStartingLocation(true);

	const onDoneSettingMapStartingLocation = (centre: Coordinate | undefined, zoom: number) => {
		setValue('starting_location', { centre, zoom }, { shouldDirty: true });
		setIsSettingMapStartingLocation(false);
	};

	const onClearMapStartingLocation = () => setValue('starting_location', null, { shouldDirty: true });

	const onCloseSettingMapStartingLocation = () => setIsSettingMapStartingLocation(false);
	// ######################
	// Starting Location (End)
	// ######################

	// ######################
	// Location Search
	// ######################
	const onClickLocationSearch = () =>
		setValue('location_search_enabled', !location_search_enabled, { shouldDirty: true });
	// ######################
	// Location Search (End)
	// ######################

	// ######################
	// Form Management
	// ######################
	const onClickSave = () => {
		handleSubmit(onDoneWithForm)();
	};

	const onDoneWithForm: SubmitHandler<MapModifiableProps> = (data) => {
		if (isEmpty(data) === false) {
			if (map === undefined && onDoneAdding !== undefined) {
				const mapData: NewMap = {
					...data,
					// For some reason when we we're saving a Map, MapForm was turning a null hero_icon into an empty {} object, rather than retaining it as null.
					// No amount of playing with the Yup Resolver that was possibly causing this could get us to make it return null rather than {}.
					// So oh well, this hack will do.
					hero_icon: JSON.stringify(data.hero_icon) !== '{}' ? data.hero_icon : null,
					starting_location: JSON.stringify(data.starting_location) !== '{}' ? data.starting_location : null,
				};
				onDoneAdding(mapData);
			} else if (map !== undefined && onDoneEditing !== undefined) {
				const mapData: MapaMap = {
					...map,
					...data,
					// For some reason when we we're saving a Map, MapForm was turning a null hero_icon into an empty {} object, rather than retaining it as null.
					// No amount of playing with the Yup Resolver that was possibly causing this could get us to make it return null rather than {}.
					// So oh well, this hack will do.
					hero_icon: JSON.stringify(data.hero_icon) !== '{}' ? data.hero_icon : null,
					starting_location: JSON.stringify(data.starting_location) !== '{}' ? data.starting_location : null,
				};
				onDoneEditing(mapData);
			}
		}
	};

	const onClose = () => navigate('/MapManager');

	const onCancelForm = () => {
		if (isDirty === true) {
			setIsDiscardChangesDialogShown(true);
		} else {
			onClose();
		}
	};
	// ######################
	// Form Management (End)
	// ######################

	// ######################
	// Discard Changes
	// ######################
	const [isDiscardChangesDialogShown, setIsDiscardChangesDialogShown] = useState(false);

	const onConfirmDiscardChanges = () => onClose();

	const onCancelDiscardChangesDialog = () => setIsDiscardChangesDialogShown(false);
	// ######################
	// Discard Changes (End)
	// ######################

	return (
		<React.Fragment>
			{isDiscardChangesDialogShown === true && (
				<DiscardChangesDialog onNo={onCancelDiscardChangesDialog} onYes={onConfirmDiscardChanges} />
			)}

			<DialogWithTransition onClose={onCancelForm}>
				<AppBar color="secondary" sx={{ position: 'sticky' }}>
					<Toolbar>
						<IconButton edge="start" color="inherit" onClick={onCancelForm}>
							<CloseIcon />
						</IconButton>

						<Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
							Map
						</Typography>

						<LoadingButton loading={isMapSaving} color="inherit" onClick={onClickSave}>
							{/* See the note re browser crashes when translating pages: https://mui.com/material-ui/react-button/#loading-button */}
							<span>Save</span>
						</LoadingButton>
					</Toolbar>
				</AppBar>

				<form onSubmit={handleSubmit(onDoneWithForm)}>
					<Paper elevation={0} sx={{ m: 3 }}>
						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormGroup>
								<Controller
									name="name"
									control={control}
									render={({ field }) => <TextFieldWithout1Password {...field} label="Name" />}
								/>
							</FormGroup>

							{errors.name && <FormHelperText error>{errors.name.message}</FormHelperText>}
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormSectionHeading>Map icon</FormSectionHeading>

							<FormGroup>
								<Typography variant="body2">Create an icon to represent your map.</Typography>

								<Button
									variant="outlined"
									startIcon={<TuneIcon />}
									onClick={onSetMapHeroIcon}
									sx={{ mt: 2, mb: 2, maxWidth: 350 }}
								>
									Set Icon
								</Button>

								{(hero_icon === undefined || isEmpty(hero_icon) === true) && (
									<Typography variant="caption">No icon has been set</Typography>
								)}
							</FormGroup>

							{errors.hero_icon && <FormHelperText error>{errors.hero_icon.message}</FormHelperText>}
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormSectionHeading>Default symbology</FormSectionHeading>

							<FormGroup>
								<Typography variant="body2">
									You can set symbology defaults for this map. These will be replaced by any symbology set by schemas or
									individual features.
								</Typography>

								<Button
									variant="outlined"
									startIcon={<TuneIcon />}
									onClick={onSetDefaultSymbolField}
									sx={{ mt: 2, mb: 2, maxWidth: 350 }}
								>
									Set Defaults
								</Button>

								{default_symbology !== undefined && isEmpty(default_symbology) === false && (
									<Typography variant="caption">Defaults have been set</Typography>
								)}

								{(default_symbology === undefined || isEmpty(default_symbology) === true) && (
									<Typography variant="caption">No defaults are set</Typography>
								)}
							</FormGroup>

							{errors.default_symbology && <FormHelperText error>{errors.default_symbology.message}</FormHelperText>}
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormGroup>
								<FormSectionHeading>Schemas</FormSectionHeading>

								<Typography variant="body2">
									Choose the schemas you would like to use on this map. If schemas are in use on the map, they will be
									unable to be removed.
								</Typography>
							</FormGroup>
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormGroup>
								<InputLabel>Schemas</InputLabel>

								<Controller
									name="available_schema_ids"
									control={control}
									render={({ field }) => (
										<Select
											{...field}
											multiple
											input={<OutlinedInput label="Schemas" />}
											renderValue={(selected) => (
												<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
													{schemas
														.filter((schema) => selected.includes(schema.id) === true)
														.map((schema) => (
															<Chip key={schema.id} label={schema.name} />
														))}
												</Box>
											)}
										>
											{schemas.map((schema) => (
												<MenuItem key={schema.id} value={schema.id} disabled={schemaIdsUsedOnMap.includes(schema.id)}>
													<Checkbox checked={available_schema_ids.includes(schema.id) === true} />
													<ListItemText primary={schema.name} />
												</MenuItem>
											))}
										</Select>
									)}
								/>
							</FormGroup>

							{errors.available_schema_ids && (
								<FormHelperText error>{errors.available_schema_ids.message}</FormHelperText>
							)}
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormGroup>
								<FormSectionHeading>Starting location</FormSectionHeading>

								<Typography variant="body2">
									Choose where your map starts off when it loads. If you don&apos;t set a location, we&apos;ll use the
									device&apos;s GPS location and zoom in to street-level.
								</Typography>

								<Button
									variant="outlined"
									startIcon={<TuneIcon />}
									onClick={onClickChooseMapStartingLocation}
									sx={{
										mt: 2,
										mb: starting_location !== null ? 0 : 2,
										maxWidth: 350,
									}}
								>
									Choose Starting Location
								</Button>

								{starting_location !== null && (
									<Button
										variant="outlined"
										startIcon={<ClearIcon />}
										onClick={onClearMapStartingLocation}
										sx={{ mt: 2, mb: 2, maxWidth: 150 }}
									>
										Clear
									</Button>
								)}

								<Typography variant="caption">
									{starting_location === null
										? 'No starting location has been set.'
										: 'A starting location has been set'}
								</Typography>
							</FormGroup>

							{errors.starting_location && <FormHelperText error>{errors.starting_location.message}</FormHelperText>}
						</FormControl>

						<FormControl fullWidth={true} sx={{ mb: 3 }} component="fieldset" variant="outlined">
							<FormGroup>
								<FormSectionHeading>Location search</FormSectionHeading>

								<Typography variant="body2">
									Choose if the map will show the control that lets people search for place names and addresses.
								</Typography>

								<List disablePadding>
									<ListItem disablePadding disableGutters>
										<ListItemButton dense onClick={onClickLocationSearch}>
											<ListItemIcon>
												<Controller
													name="location_search_enabled"
													control={control}
													defaultValue={false}
													render={({ field }) => <Checkbox {...field} checked={field.value} />}
												/>
											</ListItemIcon>
											<ListItemText primary="Location search enabled?" />
										</ListItemButton>
									</ListItem>
								</List>
							</FormGroup>

							{errors.location_search_enabled && (
								<FormHelperText error>{errors.location_search_enabled.message}</FormHelperText>
							)}
						</FormControl>
					</Paper>
				</form>
			</DialogWithTransition>

			{isSettingDefaultSymbol === true && (
				<SymbologyFieldEditor
					symbol={default_symbology}
					onDone={onDoneSettingDefaultSymbol}
					onCancel={onCancelSettingDefaultSymbol}
					nameFieldRequired={false}
					iconFieldRequired={false}
				/>
			)}

			{isSettingMapHeroIcon === true && (
				<SymbologyFieldEditor
					symbol={hero_icon}
					onDone={onDoneSettingMapHeroIcon}
					onCancel={onCancelSettingMapHeroIcon}
					nameFieldRequired={false}
					iconFieldRequired={true}
				/>
			)}

			{isSettingMapStartingLocation === true && (
				<MapStartingLocationEditor
					centre={starting_location?.centre}
					zoom={starting_location?.zoom}
					onDone={onDoneSettingMapStartingLocation}
					onClose={onCloseSettingMapStartingLocation}
				/>
			)}
		</React.Fragment>
	);
}

export default MapForm;
